import {Amplify} from 'aws-amplify'
import {
  deleteUser,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  signInWithRedirect,
  SignInWithRedirectInput,
  signOut,
  updateUserAttributes,
  UpdateUserAttributesOutput
} from 'aws-amplify/auth'
import {cognitoUserPoolsTokenProvider} from 'aws-amplify/auth/cognito'
import {CookieStorage} from 'aws-amplify/utils'
import axios from 'axios'
import {anyPass, isNil, omit, reject} from 'ramda'

import AnalyticsUtils from '../../analytics/utils'
import {UserTier} from '../../offer/business/privateDeals'
import {getAmplifyConfig, getConfigByHostName} from '../amplifyConfig/getConfig'
import {USER_INFO_API_ENDPOINT} from '../constants'
import {UserInfoResponse} from '../types/Auth'
import {User, UserAttributes, UserPoolId, UserPoolName} from '../types/Cognito'

export {Hub} from 'aws-amplify/utils'

/**
 * Configure Amplify
 */
export const configureAmplify = () => {
  const amplifyConfig = getAmplifyConfig(location.origin)

  Amplify.configure(amplifyConfig)

  cognitoUserPoolsTokenProvider.setKeyValueStorage(
    new CookieStorage(amplifyConfig.cookieStorage as CookieStorage)
  )

  return amplifyConfig
}

/**
 * Amplify SignOut
 */
export const signOutService = async () => {
  await signOut()
  AnalyticsUtils.resetAnalytics()
}

/**
 * Amplify Federated Social Login
 * @param provider - Provider to use for login
 */
export const socialLoginService = async (
  provider: SignInWithRedirectInput['provider']
) => {
  try {
    await signInWithRedirect({
      provider
    })
  } catch (error) {
    console.log(error)
  }
}

interface UserWithPoolId {
  user: User | null
  userPoolId: UserPoolId | ''
}

/**
 * Amplify Get current user
 * @returns UserWithPoolId
 */
export const getCurrentUserService = async (): Promise<UserWithPoolId> => {
  const {aws_user_pools_id} = getConfigByHostName(location.origin)
  const nullableUser: UserWithPoolId = {user: null, userPoolId: ''}

  try {
    const {idToken} = await getUserSessionService()

    if (idToken) {
      const currentUser = await getCurrentUser()
      const attributes = await getUserAttributesService()

      const {createdAt, updatedAt} = idToken.payload as {
        createdAt: string
        updatedAt: string
      }

      const user: User = {
        ...currentUser,
        id: attributes.sub,
        attributes,
        createdAt,
        updatedAt
      }

      return {user, userPoolId: aws_user_pools_id as UserPoolId}
    }
  } catch (error) {
    return nullableUser
  }

  return nullableUser
}

/**
 * Wrapper to get user info from server (Extract from vio_wl_context cookie)
 * @returns UserInfoResponse['user'] | null
 */
export const getUserInfoFromServer = async (): Promise<
  UserInfoResponse['user'] | null
> => {
  try {
    const response = await axios.get<UserInfoResponse>(USER_INFO_API_ENDPOINT)

    return response?.data?.user
  } catch (err) {
    console.info('Failed to fetch user info:', err)
    return null
  }
}

/**
 * Amplify fetchAuthSession() wrapper method
 * @param forceRefresh - Force refresh the session
 */
export const getUserSessionService = async (forceRefresh = false) => {
  const {tokens} = (await fetchAuthSession({forceRefresh})) || {}

  return {
    idToken: tokens?.idToken,
    accessToken: tokens?.accessToken
  }
}

/**
 * Amplify fetchAuthSession() wrapper method
 */
export const getUserAttributesService = async (): Promise<UserAttributes> => {
  const attributes = await fetchUserAttributes()

  const {email_verified, phone_number_verified} = attributes || {}
  const isEmailVerified = email_verified === 'true'
  const isPhoneNumberVerified = phone_number_verified === 'true'

  return {
    ...attributes,
    ...(email_verified && {email_verified: isEmailVerified}),
    ...(phone_number_verified && {phone_number_verified: isPhoneNumberVerified})
  } as UserAttributes
}

/**
 * update user attribute of the current authenticated user
 * @param attributes - attributes to update
 */
export const updateUserAttributesService = async (
  attributes: UserAttributes
): Promise<UpdateUserAttributesOutput> => {
  const attributesToUpdate = reject(anyPass([isNil]))(
    omit(['email', 'phone_number'], attributes)
  )

  const response = await updateUserAttributes({
    /* @ts-expect-error: we're strongly typing UserAttributes with the values we're returning from Cognito */
    userAttributes: attributesToUpdate
  })

  return response
}

const poolMap = {
  [UserPoolId.Dev]: UserPoolName.Dev,
  [UserPoolId.EtripProd]: UserPoolName.EtripProd,
  [UserPoolId.EtripStg]: UserPoolName.EtripStg,
  [UserPoolId.FindhotelProd]: UserPoolName.FindhotelProd,
  [UserPoolId.FindhotelStg]: UserPoolName.FindhotelStg
}

/**
 * Gets the user pool name from the pool ID
 * @param userPoolId - The ID of the user pool
 * @returns The corresponding user pool name or null
 */
export const getUserPoolName = (
  userPoolId: UserPoolId | null
): UserPoolName | null => userPoolId && poolMap[userPoolId]

/**
 * Upgrade current authenticated user to specific tier
 * this function does not overwrite previous attributes nor tiers
 * @param tier - the new tier ex: UserTier.Plus
 */
export const upgradeUserTier = async (
  tier: UserTier
): Promise<UserAttributes['custom:app_data']> => {
  const attributes = await getUserAttributesService()

  const appData = JSON.parse(attributes['custom:app_data'] || '{}')

  const previousTiers = appData?.tier || []

  const newAppData = JSON.stringify({
    ...appData,
    tier: [...new Set([...previousTiers, tier])]
  })

  await updateUserAttributesService({'custom:app_data': newAppData})
  return newAppData
}

export const deleteUserService = () => deleteUser()

/**
 * SignIn/Login
 * @param email - User's email address
 * @param brand - Brand identifier
 * @param languageCode - Language code for localization
 * @param apiUrl - API endpoint URL
 */
export const startLoginSignUp = async (
  email: string,
  brand: string,
  languageCode: string,
  apiUrl: string
) => {
  const {aws_user_pools_web_client_id} = configureAmplify()

  try {
    const {data} = await axios.post(
      `${apiUrl}auth/send-email-one-time-password`,
      {
        brand,
        languageCode,
        email,
        clientId: aws_user_pools_web_client_id
      }
    )

    return data?.response
  } catch (error) {
    console.error(error)
    throw error
  }
}

/**
 * Verifies the one-time password for user authentication
 * @param email - User's email address
 * @param otp - One-time password code
 * @param session - Authentication session
 * @param userId - User's ID
 * @param apiUrl - API endpoint URL
 */
export const verifyOneTimePassword = async (
  email: string,
  otp: string,
  session: string,
  userId: string,
  apiUrl: string
) => {
  const {aws_user_pools_web_client_id} = configureAmplify()

  const {data} = await axios.post(
    `${apiUrl}auth/verify-email-one-time-password`,
    {
      email,
      otp,
      session,
      clientId: aws_user_pools_web_client_id,
      userId: userId
    }
  )

  const tokens = {
    idToken: data.response.idToken,
    accessToken: data.response.accessToken,
    refreshToken: data.response.refreshToken,
    username: data.response.userId,
    clockDrift: 5000
  }

  await cognitoUserPoolsTokenProvider.authTokenStore.storeTokens(tokens)
}
